import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'

import type { AccountDataSelection } from '../../types/accountSelection'
import type { CloudType, ProductType } from '../../types'

const safeHead = <T>(list: T[] | undefined | null): T | undefined => {
  if (!Array.isArray(list) || list.length === 0) return undefined
  return list[0]
}

const EMPTY_ACCOUNTS: AccountDataSelection[] = []

type UseCloudRootState = {
  accountSelection?: {
    selectedAccountIds?: Partial<
      Record<string, Partial<Record<CloudType, string>>>
    >
    availableAccounts?: AccountDataSelection[]
  }
}

export const useCloud = ({
  product,
  accountId,
}: {
  product: ProductType
  accountId?: string
}): CloudType => {
  const params = useParams<{ cloudType?: string; cloud?: string }>()
  const selectedAccountIds = useSelector(
    (state: UseCloudRootState) => state.accountSelection?.selectedAccountIds
  )
  const allAccounts = useSelector((state: UseCloudRootState) => {
    const accounts = state.accountSelection?.availableAccounts
    return Array.isArray(accounts) ? accounts : EMPTY_ACCOUNTS
  })

  const cloudFromParams = params.cloudType || params.cloud
  if (cloudFromParams) {
    return cloudFromParams.toUpperCase() as CloudType
  }

  if (product.toLowerCase() === 'flyout') {
    const flyoutSelections =
      (
        selectedAccountIds as Record<string, Partial<Record<CloudType, string>>>
      )?.['FLYOUT'] || selectedAccountIds?.['flyout']
    if (flyoutSelections) {
      const clouds = Object.keys(flyoutSelections)
      if (clouds.length > 0) {
        return clouds[0] as CloudType
      }
    }
  }

  if (accountId) {
    const account = allAccounts.find(
      acc => acc.id === accountId && acc.product?.toLowerCase() === product
    )
    if (account?.cloud) {
      return account.cloud
    }
  }

  const accountData = allAccounts.filter(
    account => account?.product?.toLowerCase() === product
  )
  const firstAccount = safeHead(accountData)
  if (firstAccount?.cloud) {
    return firstAccount.cloud
  }

  return 'AWS'
}
